/**
 * Custom logger utility that conditionally logs based on environment
 * - Client-side: Only logs to console in development
 * - Server-side: Always logs regardless of environment
 *
 * Errors are also reported to GlitchTip (see instrumentation.ts), which groups
 * repeats into a single issue and alerts once per issue. Lower levels are kept
 * as breadcrumbs, so a reported error arrives with the log lines that led up to
 * it. Everything Sentry-related is a no-op when no DSN is configured.
 *
 * Only use logger.error for something someone should look at. Expected
 * failures a customer can cause (bad input, an expired session) belong in
 * logger.warn, where they still show up as context but raise no alert. When a
 * catch-all handler can't tell which it caught, logger.error downgrades any
 * error marked as expected (see errors.ts) to a warning.
 */
import * as Sentry from '@sentry/nextjs'
import { isExpectedError } from '@/lib/utils/errors'

const isServer = typeof window === 'undefined'
const isDevelopment = process.env.NODE_ENV === 'development'

// Determine if we should log to console (client-side only in dev, server-side always)
const shouldLogToConsole = isServer || isDevelopment

type BreadcrumbLevel = 'debug' | 'info' | 'warning'

const describe = (arg: unknown): string => {
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`
  try {
    return JSON.stringify(arg) ?? String(arg)
  } catch {
    return String(arg)
  }
}

const joinArgs = (args: unknown[]): string => args.map(describe).join(' ')

/**
 * Sentry already records console output as breadcrumbs, so one is only added
 * by hand when nothing went to the console — the browser in production.
 */
const breadcrumb = (level: BreadcrumbLevel, args: unknown[]) => {
  if (shouldLogToConsole) return
  Sentry.addBreadcrumb({ category: 'log', level, message: joinArgs(args) })
}

/**
 * An Error is reported as an exception and grouped by where it was thrown, with
 * the rest of the log line attached. A bare message is grouped by its text with
 * the numbers taken out, so "connection 123 failed" and "connection 456 failed"
 * count as one issue rather than one per connection.
 */
const report = (args: unknown[]) => {
  const error = args.find((arg): arg is Error => arg instanceof Error)
  const message = joinArgs(args.filter(arg => arg !== error))

  if (error) {
    Sentry.captureException(error, message ? { extra: { log: message } } : undefined)
    return
  }
  Sentry.captureMessage(message, { level: 'error', fingerprint: [message.replace(/\d+/g, '<n>')] })
}

const logger = {
  log: (...args: unknown[]) => {
    if (shouldLogToConsole) {
      console.log(...args)
    }
    breadcrumb('info', args)
  },

  error: (...args: unknown[]) => {
    if (args.some(isExpectedError)) {
      logger.warn(...args)
      return
    }
    if (shouldLogToConsole) {
      console.error(...args)
    }
    report(args)
  },

  warn: (...args: unknown[]) => {
    if (shouldLogToConsole) {
      console.warn(...args)
    }
    breadcrumb('warning', args)
  },

  info: (...args: unknown[]) => {
    if (shouldLogToConsole) {
      console.info(...args)
    }
    breadcrumb('info', args)
  },

  debug: (...args: unknown[]) => {
    if (shouldLogToConsole) {
      console.debug(...args)
    }
    breadcrumb('debug', args)
  },
}

export default logger
